import { getExerciseSyntaxHint } from './exerciseEditSyntaxHints.js';

export const RAW_TEXT_PROMPTS_SETTING_KEY = 'raw_text_to_markers_prompts';

/** Default Qwen model for Raw Text → markers (Timeweb / gateway id). */
export const RAW_TEXT_DEFAULT_MODEL = 'dashscope/qwen3.5-flash';

const BASE_RULES = `You convert messy teacher paste into exercise body markers for a language-learning platform.

Rules:
1. Output ONLY the final exercise body in the required marker syntax. No explanations, no titles, no markdown code fences.
2. Follow the coding rules for this exercise type exactly.
3. Fix spelling, punctuation, and formatting mistakes in the source while converting. Do not leave typos, broken quotes, or Word junk in the output.
4. Preserve useful structure when present (numbering, line breaks, question/options layout).
5. Do not invent content that is not implied by the source (except translating or marking the correct answer when the task clearly requires it).`;

/** Extra worked examples for types where paste→markers is ambiguous. */
const WORKED_EXAMPLES = {
    fill_gaps: `Examples:
Input (translate list):
Translate
1. этот ноутбук
2. та книга

Output:
1. этот ноутбук --> [this laptop]
2. та книга --> [that book]

Gap form: [prompt/answer], [/answer], [go/], [] — see coding rules.`,

    fill_gaps_self_check: `Same gap syntax as Fill Gaps: [prompt/answer], [/answer], [go/], [].`,

    fill_gaps_ai: `Same gap syntax as Fill Gaps: [prompt/answer], [/answer], [go/], []. Optional {ai:check} places a scoped AI Check button (grades gaps since the previous marker).`,

    fill_gaps_words: `Same gap syntax as Fill Gaps: [prompt/answer], [/answer], [go/], [].`,

    fill_gaps_choice: `Example mixed lines:
My love [note:love/любовь] was strong.
I [gap:live/lived] in [multiple:a/an*/the] house. [checkbox:+] True
(Notes: [note:exact preceding phrase/tooltip] — highlight + click tooltip, not scored.)`,

    multiple_choice_long: `Example:
Input:
Mozart ___________ more than 600 pieces of music.
writes
wrote
writed
was wrote

Output:
Mozart ___________ more than 600 pieces of music.
[writes]
[wrote*]
[writed]
[was wrote]

Mark the correct option with a trailing * inside the brackets.`,

    multiple_choice_short: `Example:
I [go/went/will go*] home tomorrow.`,

    order_words: `Example:
Input:
The window was open and a bird flew into the room.

Output:
The / window / was / open / and / a / bird / flew / into / the / room .

One sentence per line. Separate words with " / ". Keep the final period as its own token when present.`,

    order_lines: `Example:
Input:
First I wake up then I brush my teeth finally I leave for school

Output:
First, I wake up.
Then I brush my teeth.
Finally, I leave for school.

One item per line in the correct order. Do not number the lines. Keep each phrase, word, or sentence on its own line.`,

    matching_columns: `Example:
Where's the camera? // In your bag.
Is your car blue? // No, it's **black**.`,

    sort_boxes: `Example:
Input (messy list):
fruit: apples, bananas, oranges
Countable — apple, book
Uncountable: water, milk

Output:
fruit // apples, bananas, oranges
Countable // apple, book
Uncountable // water, milk`,

    error_correction: `Example:
Input:
She go to school every day.
The weather are nice today.
I have went there yesterday.

Output:
She [go/goes] to school every day.
The weather [are/is] nice today.
I [have went/went] there yesterday.`,

    checklist: `Example:
[+] I did my homework.
[-] I skipped class.
[] Speak about your weekend.`,

    wordlist: `Example:
**apple** [ˈæp.əl] — яблоко
met in primary school — познакомились в начальной школе

Use a spaced em-dash ( — ) between English and Russian.`,

    wordlist_slides: `Example:
**apple** [ˈæp.əl] // яблоко`,

    slides: `Example:
Front side // Back side`,

    sentence_slides: `Example:
I [live/живу] in a house. // Я [живу] в доме.`,

    image_fill_gaps: `Example:
cat // https://example.com/cat.png`,

    image_matching: `Example:
cat // https://example.com/cat.png`,
};

/**
 * Code-built default system prompt for an exercise type (never deleted by UI).
 * @param {string} exerciseType
 */
export function buildDefaultRawTextSystemPrompt(exerciseType) {
    const type = String(exerciseType || '').trim() || 'unknown';
    const syntax = getExerciseSyntaxHint(type);
    const example = WORKED_EXAMPLES[type] || '';
    const parts = [
        BASE_RULES,
        `\nExercise type key: ${type}`,
        `\nCoding rules for this type:\n${syntax}`,
    ];
    if (example) parts.push(`\nWorked examples:\n${example}`);
    return parts.join('\n');
}

/**
 * Assemble the prompt sent to the model.
 * @param {string} exerciseType
 * @param {{ promptOverride?: string|null, customNotes?: string|null }} [opts]
 */
export function assembleRawTextSystemPrompt(exerciseType, { promptOverride = null, customNotes = null } = {}) {
    const base = (promptOverride && String(promptOverride).trim())
        ? String(promptOverride).trim()
        : buildDefaultRawTextSystemPrompt(exerciseType);
    const notes = String(customNotes || '').trim();
    if (!notes) return base;
    return `${base}\n\nAdditional teacher notes (follow these):\n${notes}`;
}

/**
 * Strip accidental markdown fences from model output.
 * @param {string} text
 */
export function stripAiMarkersWrapper(text) {
    let s = String(text || '').trim();
    if (!s) return '';
    // ```markers ... ``` or ``` ... ```
    const fenced = s.match(/^```(?:[a-zA-Z0-9_-]+)?\s*\n?([\s\S]*?)\n?```$/);
    if (fenced) s = fenced[1].trim();
    return s;
}

/**
 * Normalize site_settings payload.
 * @param {unknown} raw
 * @returns {{ byType: Record<string, { prompt?: string, notes?: string }> }}
 */
export function normalizeRawTextPromptsSetting(raw) {
    const byType = {};
    const src = raw && typeof raw === 'object' ? (raw.byType || raw) : {};
    if (src && typeof src === 'object') {
        for (const [key, val] of Object.entries(src)) {
            if (!key || val == null) continue;
            if (typeof val === 'string') {
                byType[key] = { notes: val };
            } else if (typeof val === 'object') {
                byType[key] = {
                    prompt: typeof val.prompt === 'string' ? val.prompt : undefined,
                    notes: typeof val.notes === 'string' ? val.notes : undefined,
                };
            }
        }
    }
    return { byType };
}

export function getTypePromptConfig(setting, exerciseType) {
    const { byType } = normalizeRawTextPromptsSetting(setting);
    const key = String(exerciseType || '').trim();
    return byType[key] || {};
}
